import React, { forwardRef, useEffect } from 'react'
import * as S from './styles'

import Select, {
  CSSObjectWithLabel,
  GroupBase,
  OptionProps,
  SelectInstance,
  StylesConfig
} from 'react-select'

type SelectOption = { value: string; label: string }

interface MHSelectProps {
  id: string
  options: SelectOption[]
  defaultValue?: SelectOption
  handler?: (value: string, label: string, index: number) => void
}

const MHSelect = forwardRef<
  SelectInstance<SelectOption, false, GroupBase<SelectOption>>,
  MHSelectProps
>(({ id, options, defaultValue, handler }, ref) => {
  const ControlStyle = () => ({
    display: 'flex',
    flex: 'wrap',
    padding: '0.5rem 1rem',
    borderRadius: '5px',
    border: '1px solid var(--grey5)',
    color: 'var(--grey7)',
    cursor: 'pointer',
    background: 'var(--light)',
    minWidth: '200px'
  })

  const ValueContainerStyle = (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: '0 2rem 0 0'
  })

  const DropdownIndicatorStyle = () => ({
    display: 'flex',
    padding: '0',
    color: 'var(--grey2)',
    boxSizing: 'border-box'
  })

  const SingleValueStyle = (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: '0'
  })

  const InputStyle = (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: '0',
    padding: '0'
  })

  const MenuStyle = (provided: CSSObjectWithLabel) => ({
    ...provided,
    top: '0',
    margin: '0',
    borderRadius: '5px',
    boxShadow: '0 0 20px 3px rgba(120, 121, 241, 0.2)',
    width: 'fit-content',
    zIndex: 3
  })

  const MenuListStyle = (provided: CSSObjectWithLabel) => ({
    ...provided,
    paddingBottom: '0.5rem',
    paddingTop: '0.5rem'
  })

  const OptionStyle = (provided: CSSObjectWithLabel, state: OptionProps) => ({
    ...provided,
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? 'rgba(123, 97, 255, 0.2)'
      : 'var(--light)',
    color: 'var(--dark)',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: state.isSelected
        ? 'rgba(123, 97, 255, 0.2)'
        : 'rgba(123, 97, 255, 0.1)'
    }
  })

  const Style: StylesConfig = {
    control: ControlStyle,
    valueContainer: ValueContainerStyle,
    // @ts-expect-error check this
    dropdownIndicator: DropdownIndicatorStyle,
    singleValue: SingleValueStyle,
    input: InputStyle,
    menu: MenuStyle,
    menuList: MenuListStyle,
    option: OptionStyle
  }

  useEffect(() => {
    if (typeof ref === 'function' || !ref?.current || options?.length === 0)
      return
    const defaultOption = defaultValue ?? options[0]
    ref.current.selectOption(defaultOption)
  }, [defaultValue, options, ref])

  return (
    <S.MHSelectContainer>
      <Select
        ref={ref}
        inputId={id}
        options={options}
        placeholder={null}
        isSearchable={false}
        components={{ IndicatorSeparator: null }}
        styles={Style}
        onChange={(option: SelectOption) => {
          const optionIndex = options.indexOf(option)
          handler(option.value, option.label, optionIndex)
        }}
      />
    </S.MHSelectContainer>
  )
})

MHSelect.displayName = 'MHSelect'

export default MHSelect
